import React from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import i18n from "i18next";
import "./_visorSortableList.scss";

/* eslint-disable react/prop-types */

// Inline six-dot grip — the universal "drag me" affordance. Inline SVG (not a
// Material-icon ligature) so it never depends on an icon-font subset being
// loaded, and has a fixed footprint that can't overflow the card.
function GripIcon() {
  return (
    <svg
      className="vsl-grip"
      width="12"
      height="20"
      viewBox="0 0 12 20"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="4" cy="4" r="1.4" />
      <circle cx="8" cy="4" r="1.4" />
      <circle cx="4" cy="10" r="1.4" />
      <circle cx="8" cy="10" r="1.4" />
      <circle cx="4" cy="16" r="1.4" />
      <circle cx="8" cy="16" r="1.4" />
    </svg>
  );
}

// Shared learner-facing (Visor) sortable list used by the drag-to-reorder
// assessments (Matching right column, Ordering). It replaces the old jQuery-UI
// sortable + `swap_vert` handle, which learners read as a button. Affordances
// per the UX brief: the WHOLE card drags, a six-dot grip is the visual cue,
// grab/grabbing cursors, hover elevation, a drop-placeholder gap, explicit
// up/down move buttons (touch + keyboard fallback), full keyboard sorting
// (Space to pick up, arrows to move) and screen-reader announcements.
//
// Controlled component: the parent owns the order. `items` is an array of
// { id, content, status? } already in display order; `onReorder(newIdOrder)`
// fires with the reordered id list. Scoring/shuffle/submit stay in the parent.

function SortableRow({ id, index, total, content, status, disabled, onMove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  // Drag happens IN PLACE (no floating overlay): the card slides vertically and
  // the others shift to open a gap. Keep it fully opaque and lift it above its
  // siblings while dragging.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 20, position: "relative" } : {}),
  };

  const statusClass = status ? " vsl-row--" + status : "";

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={"vsl-row" + (isDragging ? " vsl-row--dragging" : "") + statusClass}
    >
      {/* Up/down move buttons — touch + keyboard-free alternative to dragging. */}
      <div className="vsl-move" contentEditable={false}>
        <button
          type="button"
          className="vsl-move-btn"
          aria-label={i18n.t("messages.move_up") || "Move up"}
          title={i18n.t("messages.move_up") || "Move up"}
          disabled={disabled || index === 0}
          onClick={() => onMove(index, -1)}
        >
          ▲
        </button>
        <button
          type="button"
          className="vsl-move-btn"
          aria-label={i18n.t("messages.move_down") || "Move down"}
          title={i18n.t("messages.move_down") || "Move down"}
          disabled={disabled || index === total - 1}
          onClick={() => onMove(index, 1)}
        >
          ▼
        </button>
      </div>

      {/* Live position badge. */}
      <span className="vsl-badge" aria-hidden="true">
        {index + 1}
      </span>

      {/* The whole card is the drag handle (dnd-kit listeners + a11y attrs).
          PointerSensor's activation distance lets taps on inner controls still
          register as clicks instead of starting a drag. */}
      <div className="vsl-card" {...(disabled ? {} : attributes)} {...(disabled ? {} : listeners)}>
        <GripIcon />
        <div className="vsl-body">{content}</div>
      </div>

      {status === "correct" ? (
        <i className="vsl-status material-icons correct" aria-hidden="true">
          done
        </i>
      ) : null}
      {status === "incorrect" ? (
        <i className="vsl-status material-icons incorrect" aria-hidden="true">
          clear
        </i>
      ) : null}
    </li>
  );
}

export default function VisorSortableList({ items, onReorder, disabled, ariaLabel }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = items.map((it) => it.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  const move = (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= ids.length) return;
    onReorder(arrayMove(ids, index, newIndex));
  };

  // Screen-reader narration of pick-up / move / drop, referencing 1-based slots.
  const announcements = {
    onDragStart({ active }) {
      return `Picked up item ${ids.indexOf(active.id) + 1} of ${ids.length}.`;
    },
    onDragOver({ active, over }) {
      if (!over) return undefined;
      return `Item ${ids.indexOf(active.id) + 1} moved to position ${ids.indexOf(over.id) + 1} of ${ids.length}.`;
    },
    onDragEnd({ active, over }) {
      if (!over) return `Item dropped.`;
      return `Item dropped at position ${ids.indexOf(over.id) + 1} of ${ids.length}.`;
    },
    onDragCancel() {
      return `Reordering cancelled.`;
    },
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{ announcements }}
      // Lock dragging to the vertical axis and keep the card inside the list —
      // the card can only move up/down and never escapes its box.
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className="vsl-list" role="list" aria-label={ariaLabel}>
          {items.map((it, i) => (
            <SortableRow
              key={it.id}
              id={it.id}
              index={i}
              total={items.length}
              content={it.content}
              status={it.status}
              disabled={disabled}
              onMove={move}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
/* eslint-enable react/prop-types */
